import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { getPluginIdForAccount } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-account";

const NUMBERED_SUFFIX_PATTERN = /^(.*)\s\((\d+)\)$/;

const baseNameOf = (name: string): string => {
  const trimmed = name.trim();
  return NUMBERED_SUFFIX_PATTERN.exec(trimmed)?.[1] ?? trimmed;
};

/**
 * Appends " (1)", " (2)", ... to a proposed account name only when it collides with an
 * already-saved name or a sibling in the same batch (e.g. two Opal cards for the same person —
 * the plugin API gives no other distinguishing label). Ignores any existing "(n)" suffix when
 * comparing, so a later duplicate continues the count instead of restarting it.
 *
 * Tolerates a falsy `name` at runtime despite the `string` type — Opal, at least, mixes in
 * unnamed placeholder entries (id present, name null, balance 0) alongside real cards — by
 * passing it through unchanged instead of throwing on `.trim()`.
 *
 * `currentNames[i]` is the name `proposedNames[i]` is already saved under, if it's in the vault.
 * It's kept as long as the plugin still reports the same base name, so a resync never renumbers
 * cards that were added one at a time ("Anthea", then "Anthea (1)"). A rename at the source
 * changes the base name and comes through as before. Only the first account in the batch keeps a
 * given current name, so two cards already saved under the identical name still get separated.
 * Every current name also counts as taken (like `existingNames`), so a new card never reuses a
 * kept card's "(n)" even when the caller's `existingNames` misses that card.
 */
export function disambiguateAccountNames(
  proposedNames: string[],
  existingNames: string[],
  currentNames: (string | undefined)[] = [],
): string[] {
  const usedNumbersByBase = new Map<string, Set<number>>();
  const unnumberedBases = new Set<string>();

  const recordExisting = (name: string | undefined): void => {
    // Skips an absent currentNames entry (a card not yet in the vault) and, at runtime, a null
    // name — a pre-existing account saved by an older version of this code can have name: null
    // (Opal's unnamed placeholder entries used to be saved as-is, before isNamedPluginAccount
    // started filtering them out).
    if (!name) {
      return;
    }
    const trimmed = name.trim();
    const match = NUMBERED_SUFFIX_PATTERN.exec(trimmed);
    const base = (match ? match[1] : trimmed).toLowerCase();
    if (match) {
      const numbers = usedNumbersByBase.get(base) ?? new Set<number>();
      numbers.add(Number(match[2]));
      usedNumbersByBase.set(base, numbers);
    } else {
      unnumberedBases.add(base);
    }
  };
  // currentNames too: a kept saved name must reserve its "(n)" even if the caller didn't also
  // list it in existingNames (#5946).
  [...existingNames, ...currentNames].forEach(recordExisting);

  const siblingCounts = new Map<string, number>();
  proposedNames.forEach((name) => {
    if (!name) {
      return;
    }
    const base = baseNameOf(name).toLowerCase();
    siblingCounts.set(base, (siblingCounts.get(base) ?? 0) + 1);
  });

  const keptCurrentNames = new Set<string>();
  return proposedNames.map((name, index) => {
    if (!name) {
      return name;
    }
    const base = baseNameOf(name);
    const baseLower = base.toLowerCase();

    const current = currentNames[index]?.trim();
    if (
      current &&
      baseNameOf(current).toLowerCase() === baseLower &&
      !keptCurrentNames.has(current.toLowerCase())
    ) {
      keptCurrentNames.add(current.toLowerCase());
      return current;
    }
    const collides =
      unnumberedBases.has(baseLower) ||
      (usedNumbersByBase.get(baseLower)?.size ?? 0) > 0 ||
      (siblingCounts.get(baseLower) ?? 0) > 1;

    if (!collides) {
      return name;
    }

    const usedNumbers = usedNumbersByBase.get(baseLower) ?? new Set<number>();
    let candidate = 1;
    while (usedNumbers.has(candidate)) {
      candidate++;
    }
    usedNumbers.add(candidate);
    usedNumbersByBase.set(baseLower, usedNumbers);
    return `${base} (${candidate})`;
  });
}

/** Raw names of this plugin's accounts already in the vault, used to disambiguate a
 * same-named card (e.g. a second "Anthea" Opal card) — see PluginAccountsMap.toAccountView's
 * existingAccountNames param.
 *
 * Scoped by plugin id (getPluginIdForAccount), not institution: most plugins share the generic
 * "Other" institution, so an institution filter counted gift-card/blockstream/ethvm/... names
 * against each other, and missed Opal cards saved before Opal got its own institution (they're
 * still linked to "Other"). The plugin id also rules out manual accounts sharing a name.
 *
 * Includes accounts in the current fetch batch: on a resync, disambiguateAccountNames keeps each
 * one's saved name via its currentNames param instead of renumbering it.
 *
 * Uses `originalName`, not `displayName`: the incoming `proposedNames` in
 * PluginAccountsMap.toAccountView are raw, un-nicknamed institution names, so comparing them
 * against a nicknamed `displayName` would miss a real collision (existing "Anthea" renamed to
 * "Emergency Fund" no longer matches a freshly-fetched second "Anthea") and could flag a false
 * one (a nickname that happens to coincide with another account's raw name). */
export function existingPluginAccountNames(
  accountViews: AccountView[],
  pluginId: string | undefined,
): string[] {
  // getPluginIdForAccount is undefined for every non-plugin account, so an unknown plugin id
  // must match nothing rather than every manual/bank account.
  if (!pluginId) {
    return [];
  }
  return accountViews
    .filter((account) => getPluginIdForAccount(account) === pluginId)
    .map((account) => account.originalName);
}
